import { useCallback, useEffect, useRef, useState } from "react";
import * as api from "../../api";
import Icon from "../Icon";
import { haceCuanto } from "./estadoNotion";

const UN_DIA_MS = 86400000;
// 5.000, 1.204: el punto de miles tambien en cuatro cifras (toLocaleString de es no lo pone ahi)
const num = n => String(Math.round(Number(n))).replace(/\B(?=(\d{3})+(?!\d))/g, ".");

// Lo que dice la fila de un nodo: «Página · 4 páginas dentro», «Base · 340 filas», «Base · 340 filas · mostrando las primeras 5.000».
export function subtitulo(h) {
  if (h.tipo === "espacio") return "Espacio de Notion";
  if (h.tipo === "base") {
    const filas = Number.isFinite(h.filas) ? `${num(h.filas)} ${h.filas === 1 ? "fila" : "filas"}` : "filas sin contar";
    return h.truncada ? `Base · mostrando las primeras ${num(h.filas_leidas || h.filas)} filas` : `Base · ${filas}`;
  }
  return h.tiene_hijos ? "Página · con páginas dentro" : "Página · sin subpáginas";
}

// Explorador de Notion (T-139): se navega por lo que la persona compartio con ALM hasta llegar a su lista y se elige una base.
// Con un solo espacio se entra directo en el; con varios, empieza por la lista de espacios. Las filas de una base no son nodos: se elige
// la base entera (boton «Usar como mi lista de tareas», o Intro sobre ella). Arbol con teclado: ↑ ↓ mueven, → o Intro entran,
// ← o Retroceso suben.
//
// onElegir(base): la persona ha elegido una base ({ id, titulo, icono, url, filas, truncada }).
// onConectar(): reconectar o conectar Notion (compartir mas paginas, o sin conexion).
export default function ExploradorNotion({ onElegir, onConectar }) {
  const [sitio, setSitio] = useState({ espacio: "", id: "" });   // donde estamos; vacio = el principio
  const [datos, setDatos] = useState(null);                       // respuesta de /notion/explorar
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);                       // { codigo, mensaje }
  const [q, setQ] = useState("");
  const [seleccion, setSeleccion] = useState(null);               // id de la base con el boton abierto
  const [activo, setActivo] = useState(0);
  const [actualizando, setActualizando] = useState(false);
  const [mas, setMas] = useState(false);
  const [variosEspacios, setVariosEspacios] = useState(false);
  const lista = useRef(null);
  const peticion = useRef(0);

  const buscando = q.trim().length >= 2;

  const cargar = useCallback(async (params, { sumar = false } = {}) => {
    const n = ++peticion.current;
    if (!sumar) { setCargando(true); setError(null); }
    else setMas(true);
    try {
      const d = await api.exploradorNotion(params);
      if (n !== peticion.current) return;
      setDatos(prev => (sumar && prev ? { ...d, hijos: [...prev.hijos, ...(d.hijos || [])] } : d));
      if (!params.espacio && !params.id && !params.q) setVariosEspacios(Array.isArray(d.hijos) && d.hijos.length > 1);
      // Con un solo espacio conectado se entra directo en el
      if (!params.espacio && !params.id && !params.q && d.nodo === null && Array.isArray(d.hijos) && d.hijos.length === 1 && d.hijos[0].tipo === "espacio") {
        setSitio({ espacio: String(d.hijos[0].id), id: "" });
      }
    } catch (e) {
      if (n !== peticion.current) return;
      setError({ codigo: e?.code || "", mensaje: e?.message || "No se ha podido leer Notion" });
      setDatos(null);
    } finally {
      if (n === peticion.current) { setCargando(false); setMas(false); }
    }
  }, []);

  // Donde estamos o lo buscado
  useEffect(() => {
    if (buscando) {
      const t = setTimeout(() => cargar({ espacio: sitio.espacio || undefined, q: q.trim() }), 300);
      return () => clearTimeout(t);
    }
    cargar({ espacio: sitio.espacio || undefined, id: sitio.id || undefined });
    return undefined;
  }, [sitio, q, buscando, cargar]);

  useEffect(() => { setActivo(0); setSeleccion(null); }, [datos?.nodo?.id, buscando]);

  const hijos = datos?.hijos || [];
  const migas = datos?.migas || [];
  const enElPrincipio = !sitio.espacio && !sitio.id;
  const nombreNivel = datos?.nodo?.titulo || (sitio.espacio ? "Notion" : "Notion");

  const entrar = h => {
    setQ("");
    if (h.tipo === "espacio") setSitio({ espacio: String(h.id), id: "" });
    else setSitio(s => ({ espacio: s.espacio, id: String(h.id) }));
  };
  // Subir un nivel: el ultimo de las migas, o la raiz del espacio
  const subir = () => {
    if (buscando) { setQ(""); return; }
    if (migas.length > 1) setSitio(s => ({ ...s, id: String(migas[migas.length - 1].id) }));
    else if (sitio.id) setSitio(s => ({ ...s, id: "" }));
    else setSitio({ espacio: "", id: "" });
  };
  const irA = m => { setQ(""); setSitio(s => ({ espacio: s.espacio, id: m.tipo === "espacio" ? "" : String(m.id) })); };

  const abrir = h => {
    if (h.tipo === "base") setSeleccion(s => (s === h.id ? null : h.id));
    else entrar(h);
  };
  const elegir = h => onElegir?.({ id: h.id, titulo: h.titulo, icono: h.icono, url: h.url, filas: h.filas, truncada: !!h.truncada });

  const actualizar = async () => {
    setActualizando(true);
    try { await api.syncNotion(); } catch { /* el error de la lectura de abajo lo dira */ }
    setActualizando(false);
    setSitio(s => ({ ...s }));
  };

  const teclado = e => {
    // Los controles de dentro (buscador, botones) llevan su propio teclado
    if (["INPUT", "BUTTON", "SELECT", "TEXTAREA"].includes(e.target.tagName)) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActivo(a => Math.min(hijos.length - 1, a + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActivo(a => Math.max(0, a - 1)); }
    else if (e.key === "Home") { e.preventDefault(); setActivo(0); }
    else if (e.key === "End") { e.preventDefault(); setActivo(Math.max(0, hijos.length - 1)); }
    else if ((e.key === "ArrowRight" || e.key === "Enter") && hijos[activo]) {
      e.preventDefault();
      const h = hijos[activo];
      if (h.tipo === "base") { if (e.key === "Enter") elegir(h); else setSeleccion(h.id); }
      else entrar(h);
    }
    else if (e.key === "ArrowLeft" || e.key === "Backspace") { e.preventDefault(); if (!enElPrincipio) subir(); }
  };
  useEffect(() => { lista.current?.querySelectorAll("[role=treeitem]")[activo]?.focus?.(); }, [activo]);

  const viejo = datos?.espejo_de && Date.now() - Date.parse(datos.espejo_de) > UN_DIA_MS;

  // ── Errores ──────────────────────────────────────────────────────────────
  if (error) {
    const sinConexion = error.codigo === "SIN_CONEXION";
    const noEncontrado = error.codigo === "NO_ENCONTRADO";
    return (
      <div className="ex">
        <div className={"mt-plan-aviso" + (sinConexion || noEncontrado ? "" : " ex-err")} role="alert">
          {sinConexion ? <span><b>Notion no está conectado.</b> Conéctalo para elegir tu lista.</span>
            : noEncontrado ? <span><b>Esa página ya no está compartida.</b> Solo veo lo que compartiste con ALM en Notion.</span>
            : <span><b>No se ha podido leer Notion.</b> Tu lista no ha cambiado.</span>}
          {sinConexion && onConectar && <button type="button" className="mt-b p btn" onClick={onConectar}>Conectar Notion</button>}
          {noEncontrado && <button type="button" className="mt-b btn" onClick={() => { setError(null); setSitio({ espacio: "", id: "" }); }}>Volver al principio</button>}
          {!sinConexion && !noEncontrado && <button type="button" className="mt-b btn" onClick={() => setSitio(s => ({ ...s }))}>Reintentar</button>}
        </div>
      </div>
    );
  }

  return (
    <div className="ex" onKeyDown={teclado}>
      <label htmlFor="ex-q" className="sr-only">Buscar en Notion</label>
      <input id="ex-q" className="mt-inp" type="search" placeholder="Buscar por título en todo lo que has compartido…" value={q}
        onChange={e => setQ(e.target.value)} />
      {q.trim().length === 1 && <p className="mt-sel-nota">Escribe al menos dos letras.</p>}

      {/* Con un solo espacio, su raiz es el principio: no hay a donde subir */}
      {!buscando && !enElPrincipio && (datos?.nodo || variosEspacios) && (
        <div className="ex-nivel">
          <button type="button" className="mt-b btn ex-atras" onClick={subir} aria-label={`Volver: ${nombreNivel}`}>‹ Atrás</button>
          <nav className="ex-migas" aria-label="Dónde estás">
            <button type="button" className="ex-miga" onClick={() => { setQ(""); setSitio({ espacio: "", id: "" }); }}>Notion</button>
            {migas.map(m => (
              <span key={m.id} className="ex-par"><span aria-hidden="true">›</span>
                <button type="button" className="ex-miga" onClick={() => irA(m)}>{m.titulo}</button></span>
            ))}
            {datos?.nodo && <span className="ex-par"><span aria-hidden="true">›</span><b>{datos.nodo.titulo}</b></span>}
          </nav>
        </div>
      )}

      {datos?.espejo_de && (
        <div className={"ex-datos" + (viejo ? " viejo" : "")}>
          <span>{viejo ? "⚠ " : ""}{viejo ? `Datos de ${haceCuanto(datos.espejo_de)}: pulsa Actualizar para ver lo último.` : `Datos de ${haceCuanto(datos.espejo_de) || "hace un tiempo"}`}</span>
          <button type="button" className="mt-b btn" disabled={actualizando} onClick={actualizar}>{actualizando ? "Actualizando…" : "Actualizar"}</button>
        </div>
      )}

      {cargando && !datos ? (
        <ul className="ex-arbol" aria-busy="true" aria-label="Leyendo Notion">
          {[60, 45, 70, 52].map(w => <li key={w} className="ex-skel"><i style={{ width: 26, height: 26, borderRadius: 7 }} /><i style={{ width: `${w}%` }} /></li>)}
        </ul>
      ) : !hijos.length ? (
        buscando ? (
          <div className="mt-vacio" role="status"><b>No hay nada con ese título</b>
            Solo busco entre lo que has compartido con ALM en Notion. Prueba con otra palabra o{" "}
            <button type="button" className="mt-b btn" onClick={() => setQ("")}>Quitar la búsqueda</button></div>
        ) : (
          <div className="mt-vacio ex-sin" role="status">
            <b>{enElPrincipio || !datos?.nodo ? "Aún no veo tu lista" : "No hay nada aquí dentro"}</b>
            Solo veo lo que compartiste con ALM en Notion.
            <ol className="ex-pasos">
              <li>Abre la página (o la base) en Notion.</li>
              <li>Pulsa <b>⋯</b> arriba a la derecha → <b>Conexiones</b> y añade <b>ALM</b>.</li>
              <li>Vuelve aquí y pulsa <b>Actualizar</b>.</li>
            </ol>
            O pulsa <b>Compartir más páginas</b> para elegir en Notion qué ver.
            <div className="mt-sel-nota">¿Tu lista es una <b>vista enlazada</b> de otra base? Elige la base original: las vistas enlazadas no se pueden leer.</div>
            <div className="mt-dlg-pie" style={{ justifyContent: "flex-start" }}>
              {onConectar && <button type="button" className="mt-b p btn" onClick={onConectar}>Compartir más páginas</button>}
              <button type="button" className="mt-b btn" disabled={actualizando} onClick={actualizar}>Actualizar</button>
            </div>
          </div>
        )
      ) : (
        <>
          <ul ref={lista} className="ex-arbol" role={buscando ? "listbox" : "tree"} aria-label={buscando ? "Resultados de la búsqueda" : `Contenido de ${nombreNivel}`}>
            {hijos.map((h, i) => {
              const base = h.tipo === "base";
              const abierto = seleccion === h.id;
              return (
                <li key={h.id} role={buscando ? "option" : "treeitem"} aria-selected={base ? abierto : undefined}
                  aria-level={buscando ? undefined : 1} tabIndex={i === activo ? 0 : -1}
                  className={"ex-nodo" + (i === activo ? " act" : "") + (abierto ? " abierto" : "")}
                  onClick={() => { setActivo(i); abrir(h); }}>
                  <div className="ex-fila">
                    <span className="ex-ic" aria-hidden="true">{h.icono && h.icono.length <= 4 ? h.icono : <Icon name={base ? "list-check" : "diagram-3"} size={13} color="currentColor" />}</span>
                    <span className="mt-sel-t">
                      <b>{h.titulo || "Sin título"}</b>
                      <small>{subtitulo(h)}{h.padre?.titulo ? ` · en ${h.padre.titulo}` : ""}</small>
                    </span>
                    {h.ya_es_fuente && <span className="ex-fuente">✓ Ya es tu lista</span>}
                    <span className={"ex-tipo" + (base ? " base" : "")}>{h.tipo === "espacio" ? "Espacio" : base ? "Base" : "Página"}</span>
                    {!base && (h.tiene_hijos || h.tipo === "espacio") && <span className="ex-ir" aria-hidden="true">›</span>}
                  </div>
                  {base && abierto && (
                    <div className="ex-elegir" onClick={e => e.stopPropagation()}>
                      <div className="mt-sel-nota">
                        {h.truncada ? `Esta base tiene muchas filas: se leen las primeras ${num(h.filas_leidas || h.filas)}. Si tus tareas están más allá, usa una base más pequeña o pide que se filtre.`
                          : "Aquí verás solo las tareas que sean tuyas (después eliges las columnas)."}
                      </div>
                      <button type="button" className="mt-b p btn" onClick={() => elegir(h)}>Usar como mi lista de tareas</button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
          {datos?.siguiente_cursor && (
            <button type="button" className="mt-b btn ex-mas" disabled={mas}
              onClick={() => cargar({ espacio: sitio.espacio || undefined, id: sitio.id || undefined, q: buscando ? q.trim() : undefined, cursor: datos.siguiente_cursor }, { sumar: true })}>
              {mas ? "Cargando…" : "Ver más"}
            </button>
          )}
          {datos && Number.isFinite(datos.total) && datos.total > hijos.length && !datos.siguiente_cursor && (
            <p className="mt-sel-nota">Hay {datos.total - hijos.length} más: afina la búsqueda.</p>
          )}
        </>
      )}
      <p className="ex-teclas">↑ ↓ mover · → o Intro entrar · ← o Retroceso subir · Intro en una base la elige</p>
    </div>
  );
}
